/**
 * Электрический элемент на чертеже — выбирает компонент по типу из каталога.
 *
 * Розетка, выключатель и распредкоробка (и щиток) — разные React-компоненты в своих файлах,
 * бра — из раздела «Освещение». Этот компонент — точка входа для холста и развёрток:
 * он смотрит на вид элемента в каталоге электрики (`ElectricalKind`) или на членство
 * в разделе освещения и отдаёт нужный компонент. Незнакомый вид рисуется как розетка.
 *
 * Общее для всех: габариты считаются из каталога с учётом укрупнения и нижней
 * границы в пикселях, цвет — из типа, выделение и «не подключён» — снаружи.
 */
import type { PointerEvent } from "react";
import type { Fixture } from "@/shared/plan";
import { isLight, isSwitch, isWiringHub, type CatalogItem } from "@/shared/catalog";
import { symbolRenderSize } from "../../CatalogSymbol";
import { LightShape } from "../../lighting/2d/LightShape";
import { JunctionBoxShape } from "./JunctionBoxShape";
import { SocketShape } from "./SocketShape";
import { SwitchShape } from "./SwitchShape";

/** Цвет и габариты, если типа в каталоге больше нет — объект всё равно виден и удаляем. */
const MISSING = { width: 80, height: 80, color: "#9aa3b2" };

export interface ElectricalItemShapeProps {
	fixture: Fixture;
	/** Тип из каталога; null — тип удалён из каталога */
	item: CatalogItem | null;
	selected: boolean;
	/** Прибор без кабеля — подсвечивается предупреждающим кольцом */
	unconnected?: boolean;
	/** Размер миллиметра в пикселях — линии постоянной толщины на экране */
	mmPerPx: number;
	interactive?: boolean;
	/** Увеличение символа: на плане он мельче стены и нуждается в укрупнении */
	scale?: number;
	/** Минимальный размер символа на экране, пикселей */
	minPx?: number;
	onPointerDown?: (e: PointerEvent<SVGGElement>) => void;
}

/** Что получает конкретный элемент: габариты уже посчитаны, цвет выбран. */
export interface ElementShapeProps {
	fixture: Fixture;
	item: CatalogItem | null;
	width: number;
	height: number;
	color: string;
	selected: boolean;
	unconnected?: boolean;
	mmPerPx: number;
	interactive?: boolean;
	onPointerDown?: (e: PointerEvent<SVGGElement>) => void;
}

export const ElectricalItemShape = ({
	fixture,
	item,
	selected,
	unconnected,
	mmPerPx,
	interactive = true,
	scale = 1,
	minPx = 0,
	onPointerDown,
}: ElectricalItemShapeProps) => {
	const size = item ?? MISSING;
	const { width, height } = symbolRenderSize(size.width, size.height, mmPerPx, scale, minPx);
	const props: ElementShapeProps = {
		fixture,
		item,
		width,
		height,
		color: size.color,
		selected,
		unconnected,
		mmPerPx,
		interactive,
		onPointerDown,
	};

	// Щиток — такой же узел проводки, как распредкоробка: заметная подложка, без «не подключён»
	if (isWiringHub(item)) return <JunctionBoxShape {...props} />;
	if (isSwitch(item)) return <SwitchShape {...props} />;
	if (isLight(item)) return <LightShape {...props} />;
	return <SocketShape {...props} />;
};
